/**
 * Today's lesson: Numi plans a short session the way a tutor would.
 * Warm-up review (spaced) → new learning or practice on the focus topic → a stretch → wrap-up.
 */
import * as E from "@numi/engine";
import { S, touch, go, prog } from "./store.ts";
import { startLearn, startRound, startQueueRound } from "./question.ts";
import { startBubbles } from "./screens/Bubbles.tsx";

export type StepKind = "warm" | "learn" | "practice" | "stretch" | "bubbles" | "wrap";
export interface QItem { tid: string; level: 1 | 2 | 3; label?: string }
export interface PlanStep { kind: StepKind; title: string; sub: string; items?: QItem[]; done: boolean }
export interface Plan { date: string; child: string; focus: string; steps: PlanStep[]; feeling?: string; next?: string }

const today = () => new Date().toLocaleDateString("en-CA");
const key = () => `numi.plan.${S.child?.id}`;
/** Accuracy over the last 10 questions when we have at least 5 of them, else lifetime (older saved progress). */
const acc = (tid: string) => {
  const p = S.progress[tid]; if (!p || !p.seen) return 1;
  const r = p.recent || ""; if (r.length >= 5) return [...r].filter((c) => c === "1").length / r.length;
  return p.right / p.seen;
};
/** Mastered = level 3, enough practice, and still accurate lately. A mastered topic can fade if recent answers slip. */
export const mastered = (tid: string) => { const p = S.progress[tid]; return !!p && p.level === 3 && p.seen >= 8 && acc(tid) >= 0.75; };
const RECHECK_MS = 7 * 864e5;

/** The topic Numi would teach next: keep going with a started topic, else the next one in syllabus order. */
export function focusTopic(): E.Topic {
  const list = E.topicsFor(S.child!.grade);
  const started = list.filter((t) => (S.progress[t.id]?.seen || 0) > 0 && !mastered(t.id)).sort((a, b) => (S.progress[b.id].last || 0) - (S.progress[a.id].last || 0));
  return started[0] || list.find((t) => !mastered(t.id)) || [...list].sort((a, b) => (S.progress[a.id]?.last || 0) - (S.progress[b.id]?.last || 0))[0];
}

function buildPlan(): Plan {
  const c = S.child!, g = c.grade, focus = focusTopic(), fp = S.progress[focus.id];
  const steps: PlanStep[] = [];
  // Warm-up: the two topics most in need of a revisit (spaced review), else a warm-up from the class below.
  const dayAgo = Date.now() - 20 * 3600e3;
  const pool = [...E.topicsFor(g), ...(g > 1 ? E.topicsFor(g - 1) : [])].filter((t) => t.id !== focus.id && (S.progress[t.id]?.seen || 0) > 0);
  const due = pool.filter((t) => (S.progress[t.id].last || 0) < dayAgo);
  const rev = (due.length ? due : pool).sort((a, b) => acc(a.id) - acc(b.id) || (S.progress[a.id].last || 0) - (S.progress[b.id].last || 0)).slice(0, 2);
  let warm: QItem[] = rev.map((t) => ({ tid: t.id, level: S.progress[t.id].level, label: `Review: ${E.topicTitle(t)}` }));
  // Memory check: a topic mastered a while ago is asked again, so mastery is kept, not just earned once.
  const stale = pool.filter((t) => mastered(t.id) && (S.progress[t.id].last || 0) < Date.now() - RECHECK_MS).sort((a, b) => (S.progress[a.id].last || 0) - (S.progress[b.id].last || 0))[0];
  if (stale) warm = [{ tid: stale.id, level: S.progress[stale.id].level, label: `Memory check: ${E.topicTitle(stale)}` }, ...warm.filter((w) => w.tid !== stale.id)].slice(0, 2);
  if (warm.length < 2 && g > 1) warm = warm.concat(E.shuffle(E.topicsFor(g - 1)).slice(0, 2 - warm.length).map((t) => ({ tid: t.id, level: 2 as const, label: `Warm-up from ${E.board().gradeWord} ${g - 1}` })));
  // Only add a warm-up block when we have at least one distinct topic to review.
  // Never pad with duplicates — a child seeing the same question three times in a row is worse than a shorter warm-up.
  if (warm.length) { steps.push({ kind: "warm", title: "Warm-up", sub: `${warm.length} quick question${warm.length === 1 ? "" : "s"} to wake up your brain`, items: warm, done: false }); }
  const ft = E.topicTitle(focus);
  if (!fp || !fp.seen) steps.push({ kind: "learn", title: `Learn: ${ft}`, sub: "Picture steps, then your turn", done: false });
  else steps.push({ kind: "practice", title: `Practise: ${ft}`, sub: `5 questions at level ${fp.level}`, done: false });
  // Stretch: a harder one on the focus topic, plus a mental-maths trick.
  const lv = Math.min(3, (fp?.level || 1) + 1) as 1 | 2 | 3;
  const trick = g >= 3 ? (E.vedicFor(g).find((t) => !mastered(t.id)) || E.vedicFor(g)[0]) : E.topic("abacus")!;
  const stretch: QItem[] = [{ tid: focus.id, level: lv, label: "Stretch" }, { tid: trick.id, level: (S.progress[trick.id]?.level || 1) as 1 | 2 | 3, label: `${g >= 3 ? "Vedic trick" : "Abacus"}: ${E.topicTitle(trick)}` }, { tid: focus.id, level: lv, label: "Stretch" }];
  steps.push({ kind: "stretch", title: "Stretch", sub: `Two harder ones and ${g >= 3 ? "a Vedic Maths trick" : "an abacus question"}`, items: stretch, done: false });
  steps.push({ kind: "bubbles", title: "Bubble Pop", sub: `Pop the answers on ${ft.toLowerCase()} in a quick game`, done: false });
  steps.push({ kind: "wrap", title: "Wrap-up", sub: "How did it go? Then teach a grown-up", done: false });
  const list = E.topicsFor(g); const next = list[list.findIndex((t) => t.id === focus.id) + 1];
  return { date: today(), child: c.id, focus: focus.id, steps, next: next?.id };
}

export function loadPlan(): Plan {
  if (S.plan && S.plan.child === S.child?.id && S.plan.date === today()) return S.plan;
  try { const p = JSON.parse(localStorage.getItem(key()) || "null"); if (p && p.date === today() && p.child === S.child?.id) { S.plan = p; return p; } } catch {}
  S.plan = buildPlan(); savePlan(); return S.plan;
}
export function savePlan() { try { localStorage.setItem(key(), JSON.stringify(S.plan)); } catch {} }
export const nextStepIndex = (p: Plan) => p.steps.findIndex((s) => !s.done);

export function runStep(i: number) {
  const p = loadPlan(), st = p.steps[i]; if (!st) return;
  S.planRunning = i; S.topic = p.focus; S.easier = false;
  if (st.kind === "learn") startLearn(true);
  else if (st.kind === "practice") startRound();
  else if (st.kind === "bubbles") startBubbles(false);
  else if (st.kind === "wrap") go("wrap");
  else startQueueRound(st.items!);
  S.planRunning = i; touch();
}
/** Called when a round that belongs to today's lesson finishes. */
export function completeRunningStep() {
  if (S.planRunning == null || !S.plan) return;
  const st = S.plan.steps[S.planRunning]; if (st) st.done = true;
  S.planJustDone = S.planRunning; S.planRunning = null; savePlan();
}
export function finishWrap(feeling: string) { if (!S.plan) return; S.plan.feeling = feeling; const w = S.plan.steps.find((s) => s.kind === "wrap"); if (w) w.done = true; savePlan(); touch(); }
export function teachBack(): E.Question {
  const p = loadPlan(); const t = E.topic(p.focus)!;
  // Vedic topics: always teach at level 1 (the core trick)
  const lv = t.mod === "vedic" ? 1 : Math.max(1, (prog(p.focus).level || 1) - 1) as 1 | 2 | 3;
  const q = t.gen(lv as 1 | 2 | 3); q.tid = p.focus; return q;
}

/** Next Vedic trick in sequence that has not yet been mastered, or null if all done. */
export function nextVedic(): E.Topic | null {
  const list = E.vedicFor(S.child!.grade);
  const idx = list.findIndex((t) => t.id === S.topic);
  if (idx < 0) return null;
  for (let i = idx + 1; i < list.length; i++) {
    if (!mastered(list[i].id)) return list[i];
  }
  return null;
}

/** After completing Abacus at the current level: "level" = go up, "bubbles" = pop, "done" = replay. */
export function abacusNextAction(): "level" | "bubbles" | "done" {
  const p = prog("abacus");
  if (p.level < 3) return "level";
  if (p.seen > 0 && p.right / p.seen >= 0.75) return "bubbles";
  return "done";
}
